/**
 * Sanitize filename by replacing unsafe characters with underscore.
 * Keeps only: letters, numbers, dot (only for extension), underscore, dash, space
 * Only the last dot is kept as file extension separator; other dots are replaced with underscore.
 * 
 * @param filename - The original filename to sanitize
 * @returns The sanitized filename
 */
export const sanitizeFilename = (filename: string): string => {
  // Trim whitespace from start and end
  filename = filename.trim();
  
  // Find the last dot to separate name and extension
  const lastDotIndex = filename.lastIndexOf('.');
  
  let name: string;
  let extension: string;
  
  if (lastDotIndex > 0) {
    // There's an extension
    name = filename.substring(0, lastDotIndex);
    extension = filename.substring(lastDotIndex); // includes the dot
  } else {
    // No extension found
    name = filename;
    extension = '';
  }
  
  // Replace dots and any unsafe characters in the name part with underscore
  // Keeps only: letters, numbers, underscore, dash, space
  name = name.replace(/[^A-Za-z0-9_\- ]/g, "_");
  
  // Sanitize extension - keep only alphanumeric and the dot
  if (extension) {
    extension = extension.replace(/[^A-Za-z0-9.]/g, "");
  }
  
  return name + extension;
};

/**
 * Create a new File object with a sanitized filename
 * 
 * @param file - The original File object
 * @returns A new File object with sanitized name
 */
export const createSanitizedFile = (file: File): File => {
  const sanitizedName = sanitizeFilename(file.name);
  
  // Create a new File object with the sanitized name
  return new File([file], sanitizedName, {
    type: file.type,
    lastModified: file.lastModified,
  });
};

/**
 * Check if a string contains JSON objects or arrays
 * 
 * @param text - The input string to check for JSON
 * @returns True if JSON is detected, false otherwise
 */
export const containsJson = (text: string): boolean => {
  if (!text) return false;
  
  // Check for JSON objects {...} or arrays [...]
  const jsonObjectPattern = /\{[^{}]*\}/;
  const jsonArrayPattern = /\[[^\[\]]*\]/;
  
  return jsonObjectPattern.test(text) || jsonArrayPattern.test(text);
};
